import {defineStore} from 'pinia';
import {computed, reactive, readonly, type Ref, ref, toRefs} from 'vue';
import {ActorDto, AthumiService, type IUserData} from "@/api/enterprise-gateway-api.proxy.ts";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {userLocalStorage} from "@/app/shared/helpers/user-local-storage.ts";
import {formatService} from "@/app/stores/athumi-service-store.ts";

const INITIAL_STATE = {
    user: <IUserData>{},
    scope: '',
    isUpdating: false,
    isLoading: false,
    isAuthenticated: false,
    isCheckingAuthentication: true
};

export interface Service {
    walletAmount: number | undefined,
    walletUnavailable: boolean,
    email: string | undefined,
    service: string
}

export const useUserStore = defineStore('user-store', () => {
    const user: Ref<IUserData> = ref(INITIAL_STATE.user);
    const scope = ref(INITIAL_STATE.scope);
    const isUpdating = ref(INITIAL_STATE.isUpdating);
    const isLoading = ref(INITIAL_STATE.isLoading);
    const isAuthenticated = ref(INITIAL_STATE.isAuthenticated);
    const isCheckingAuthentication = ref(INITIAL_STATE.isCheckingAuthentication);
    
    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const userDataRetreived = computed((): boolean => Boolean(user.value?.id));
    const authenticated = computed((): boolean => isAuthenticated.value);

    const userWallet = reactive({
        walletAmount: 0 as number | undefined,
        walletUnavailable: false,
        service: AthumiService.ELys
    });

    const services = ref(<Service[]>[]);
    
    const fullName = computed((): string => {
        let fullName = '';
        if (user.value.actorInfo?.firstname) {
            fullName += user.value.actorInfo.firstname;
        }

        if (user.value.actorInfo?.lastname) {
            if (user.value.actorInfo.firstname) {
                fullName += ' ';
            }
            fullName += user.value.actorInfo.lastname;
        }
        return fullName;
    });

    const visited = (): boolean => {
        return userLocalStorage.getItem('account_visited') ?? false;
    }
    
    const hasAddress = computed((): boolean => {
        return Boolean(user.value.actorInfo?.street && user.value.actorInfo?.streetNumber && user.value.actorInfo?.postCode && user.value.actorInfo?.city && user.value.actorInfo?.countryCode);
    });

    const hasEmail = computed(() => !!user.value.actorInfo?.email);

    const isAdmin = computed((): boolean => { 
        return user.value.isAdmin ?? false;
    });

    const isCitizen = computed((): boolean => {
        return user.value.isCitizen ?? false;
    });

    const userId = computed(() => {
        return user.value.id!;
    });
    
    const actorId = computed(() => {
        return user.value.actorInfo?.id ?? null;
    })

    const updateAuthenticationStatus = (status: boolean) => {
        isAuthenticated.value = status;
    };

    const updateCheckingStatus = (isChecking: boolean) => {
        isCheckingAuthentication.value = isChecking;
    };

    const isUserAuthenticated = async (): Promise<boolean> => {
        try {
            updateCheckingStatus(true);
            const authStatus = await apiProxy.auth_IsAuthenticated();
            updateAuthenticationStatus(authStatus);
            return authStatus;
        } finally {
            updateCheckingStatus(false);
        }
    };
    
    const updateProfileStatus = (updating: boolean) => {
        isUpdating.value = updating;
    };

    const showVat = computed((): boolean | undefined => {
        return user.value.isForeign || user.value.isOvo;
    });

    const updateLoadingStatus = (loading: boolean) => {
        isLoading.value = loading
    };

    const loadUserData = async (): Promise<void> => {
        try {
            updateLoadingStatus(true);
            await getUserData();
            updateLoadingStatus(false);
        } catch(error) {
            updateLoadingStatus(false);
            await errorStore.setError(error, true);
        }
    };    
    const updateUserData = async (): Promise<void> => {
        try {
            updateProfileStatus(true);
            await getUserData();
            updateProfileStatus(false);
        } catch(error) {
            updateProfileStatus(false);
            await errorStore.setError(error, true);
        }
    };

    const getUserData = async () => {
        userLocalStorage.setItem('account_visited', true);
        user.value = await apiProxy.auth_GetUserData();
        const actorInfo = user.value.actorInfo;
        await updateWalletInformation(actorInfo);
        await updateServiceWallets(user.value.actors);
    }
    
    const updateWalletInformation = async (actorInfo: ActorDto | undefined): Promise<void> => {
        userWallet.walletUnavailable = actorInfo?.walletUnavailable ?? true;
        userWallet.walletAmount = actorInfo?.serviceBalance ?? 0;
    };

    const updateServiceWallets = async (actors: { [key: string]: ActorDto; } | undefined): Promise<void> => {
        if (!actors) {
            services.value = [];
            return;
        }

        services.value = Object.entries(actors)
            .filter(([key, actor]) => user.value.services?.includes(key as AthumiService))
            .map(([key, actor]) => ({
                walletAmount: actor.serviceBalance,
                walletUnavailable: actor.walletUnavailable ?? true,
                email: actor.email,
                service: formatService(key as AthumiService)
            }));
    };
    
    return {
        user: computed(() => readonly(user.value)),
        ...toRefs(userWallet),
        services,
        fullName,
        hasAddress,
        hasEmail,
        scope,
        isLoading,
        isUpdating,
        isAuthenticated,
        isCheckingAuthentication,
        isAdmin,
        isCitizen,
        showVat,
        userDataRetreived,
        authenticated,
        userId,
        actorId,
        visited,
        isUserAuthenticated,
        loadUserData,
        updateUserData,
    };
});

